import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/form-controls";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/components/ui/toast";
import { LOCAL_ONLY_NOTICE } from "@/lib/fixture-notice";
import { usePermission } from "@/hooks/use-permission";
import { SVC_BUNDLE_INCLUDES, SVC_RECENT_BUNDLES } from "@/data/mock";

/* SVC-04 — Support Bundle Export.
   Canonical source: prototype/pages/admin/support-bundle.html (no
   current Figma frame located). Two source-defined states: "Default"
   and "Generating" — both implemented, matching the canonical
   `startGenerate()` timing (2400ms) and its own success toast text
   verbatim. This is a client-side-only simulation: no real diagnostics
   package is created or encrypted; the canonical source itself never
   claims otherwise (there is no real backend here to package). */

const STATUS_TONE: Record<string, "info" | "success" | "neutral"> = {
  "Ready to download": "info",
  Downloaded: "success",
  Expired: "neutral",
};

export function SupportBundle() {
  const canManage = usePermission("admin:access");
  const [view, setView] = useState<"default" | "generating">("default");
  const [includes, setIncludes] = useState<Record<string, boolean>>(
    Object.fromEntries(SVC_BUNDLE_INCLUDES.map((i) => [i.label, i.defaultChecked]))
  );

  function generate() {
    setView("generating");
    setTimeout(() => {
      toast({ variant: "success", title: "Support bundle generated and encrypted — ready for download", description: LOCAL_ONLY_NOTICE });
    }, 2400);
  }

  function cancel() {
    setView("default");
    toast({ variant: "info", title: "Generation cancelled" });
  }

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <h1 className="text-xl font-extrabold text-ink">Support Bundle Export</h1>
        <div className="flex gap-1.5">
          <button
            type="button"
            onClick={() => setView("default")}
            className={`rounded-[6px] border px-3 py-1.5 text-xs font-semibold ${view === "default" ? "border-primary bg-primary text-on-primary" : "border-line bg-surface text-muted"}`}
          >
            Default
          </button>
          <button
            type="button"
            onClick={() => view === "generating" && setView("generating")}
            className={`rounded-[6px] border px-3 py-1.5 text-xs font-semibold ${view === "generating" ? "border-primary bg-primary text-on-primary" : "border-line bg-surface text-muted"}`}
            disabled={view !== "generating"}
          >
            Generating
          </button>
        </div>
      </div>

      {view === "default" && (
        <div className="space-y-5">
          <div className="rounded-[10px] border border-line bg-surface p-6">
            <div className="mb-1 text-base font-bold text-ink">Create a Support Bundle</div>
            <p className="mb-4 text-sm text-muted">Package diagnostics, service health, configuration and recent audit events for your support team. Bundles are end-to-end encrypted and expire after 14 days.</p>
            <div className="mb-4 space-y-2.5">
              {SVC_BUNDLE_INCLUDES.map((inc) => (
                <label key={inc.label} className="flex items-center gap-2.5 text-sm text-ink">
                  <Checkbox
                    checked={includes[inc.label]}
                    onCheckedChange={(v) => canManage && setIncludes((s) => ({ ...s, [inc.label]: v }))}
                    ariaLabel={inc.label}
                    disabled={!canManage}
                  />
                  {inc.label}
                </label>
              ))}
            </div>
            <Button onClick={generate} disabled={!canManage}>Generate Bundle</Button>
          </div>

          <div className="rounded-[10px] border border-line bg-surface p-6">
            <div className="mb-3 text-base font-bold text-ink">Recent Bundles</div>
            <ul className="divide-y divide-line">
              {SVC_RECENT_BUNDLES.map((b) => (
                <li key={b.filename} className="flex flex-wrap items-center justify-between gap-2 py-2.5">
                  <span className="font-mono text-xs text-ink">{b.filename}{b.status === "Expired" ? " (expired)" : ""}</span>
                  <span className="flex items-center gap-2">
                    <Badge variant={STATUS_TONE[b.status]} size="sm" dot={false}>{b.status}</Badge>
                    {b.sizeLabel && <span className="font-mono text-xs text-muted">· {b.sizeLabel}</span>}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {view === "generating" && (
        <div className="rounded-[10px] border border-line bg-surface p-8 text-center">
          <div className="mb-3 text-4xl">📦</div>
          <div className="mb-1 text-base font-bold text-ink">Generating bundle…</div>
          <p className="mb-2 text-sm text-muted">Packaging diagnostics from 5 services. This usually takes under 30 seconds.</p>
          <div className="mb-4 font-mono text-xs text-muted">bundle-harbortrust-{new Date().toISOString().slice(0, 16).replace(":", "-")}Z.zip · encrypting with org key…</div>
          <Badge variant="pending" size="sm">In progress</Badge>
          <button type="button" className="ml-4 text-sm font-semibold text-primary hover:underline" onClick={cancel}>Cancel</button>
        </div>
      )}
    </div>
  );
}
